import { eventsAuthenticatedAxios } from './axios'
import eventsPublicAxios from './axios'
import { captureDeviceInfo, type DeviceInfo } from '@/utils/deviceInfo'

export interface UserSession {
    userId: string
    userName: string
    currentStep: string
    appVersion: string
    lastActiveUtc: string
    startedUtc: string
    hasAutoSaved: boolean
    manualSaveCount: number
    idleDurationSeconds?: number
    routeDurationSeconds?: number
    userAgent?: string
    browserName?: string
    browserVersion?: string
    osName?: string
    osVersion?: string
    isMobile?: boolean
    tabId?: string
    browserSessionId?: string
}

export async function sendHeartbeat(
    userId: string, 
    userName: string, 
    step: string, 
    appVersion: string, 
    hasAutoSaved: boolean = false, 
    manualSaveCount: number = 0, 
    idleDurationSeconds: number = 0, 
    routeDurationSeconds: number = 0, 
    tabId?: string,
    browserSessionId?: string
): Promise<void> {
    // Capture device info for debugging version update issues (optional fields for backward compatibility)
    const deviceInfo = captureDeviceInfo()
    
    // Try to use public axios if user is not fully authenticated yet, or auth axios if they are
    const payload = { 
        userId, 
        userName, 
        step, 
        appVersion, 
        hasAutoSaved, 
        manualSaveCount, 
        idleDurationSeconds, 
        routeDurationSeconds, 
        userAgent: deviceInfo.userAgent, 
        browserName: deviceInfo.browserName, 
        browserVersion: deviceInfo.browserVersion, 
        osName: deviceInfo.osName, 
        osVersion: deviceInfo.osVersion, 
        isMobile: deviceInfo.isMobile, 
        platform: deviceInfo.platform,
        tabId,
        browserSessionId
    }
    
    try {
        await eventsPublicAxios.post('sessions/heartbeat', payload)
    } catch (e) {
        console.warn('❌ Failed to send heartbeat', e)
    }
}

export async function endSession(userId: string, tabId?: string, browserSessionId?: string): Promise<void> {
    const payload = { userId, tabId, browserSessionId }
    try {
        await eventsPublicAxios.post('sessions/end', payload)
    } catch (e) {
        console.warn('Failed to end session', e)
    }
}

export interface ActiveSessionsResponse {
    active: UserSession[]
    idle: UserSession[]
    admin: UserSession[]
    anonymous: UserSession[]
    allSessions: UserSession[]
}

export async function getActiveSessions(): Promise<ActiveSessionsResponse> {
    const response = await eventsPublicAxios.get<ActiveSessionsResponse>('sessions/active')
    return response.data
}

export interface RouteStats {
    visitCount: number
    totalTimeMinutes: number
    averageTimeMinutes: number
    percentage: number
}

export interface SessionStats {
    totalSessionsToday: number
    averageSessionDurationMinutes: number
    pageViews: Record<string, number>
    routeStatistics: Record<string, RouteStats>
    recentUserNames: string[]
}

export async function getSessionStats(): Promise<SessionStats> {
    const response = await eventsPublicAxios.get<SessionStats>('sessions/stats')
    return response.data
}

export async function getSessionStats7Days(): Promise<SessionStats> {
    const response = await eventsPublicAxios.get<SessionStats>('sessions/stats/7days')
    return response.data
}

export interface HourlyActivityPattern {
    hour: number // 0-23
    averageActiveSessions: number
    totalSessions: number
    peakSessions: number
    daysWithActivity: number
}

export interface DeploymentWindow {
    startHour: number
    endHour: number
    averageActivity: number
    confidence: 'high' | 'medium' | 'low'
}

export interface ActivityPatternResponse {
    hourlyPatterns: HourlyActivityPattern[]
    recommendedWindows: DeploymentWindow[]
    timezone: string
    periodStart: string
    periodEnd: string
}

export async function getActivityPatterns(): Promise<ActivityPatternResponse> {
    const response = await eventsPublicAxios.get<ActivityPatternResponse>('sessions/activity-patterns')
    return response.data
}
